/**
 * D-121 — PM web Queue health admin page.
 *
 * Reads /api/admin/workers/queue-health (require_pm-gated server-side); sorts
 * rows by queue_bytes_photos DESC; renders one row per worker with colored
 * cells per D-119 thresholds.
 *
 * Bilingual AR/EN with full RTL via the page-level html dir attribute (set by
 * IntlBoot at app boot). Latin digits for asset/queue numerics regardless of
 * locale per spec.
 */
import { FormattedMessage } from "react-intl";
import { useQueueHealth } from "../../hooks/useQueueHealth";
import { formatDateTime } from "../../lib/formatDateTime";

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

export function QueueHealthPage() {
  const { data: rows = [], isLoading, isError } = useQueueHealth();

  // D-121 — sort by queue_bytes_photos DESC (server already sorts, but the
  // dashboard re-sorts defensively in case the wire shape evolves).
  const sorted = [...rows].sort((a, b) => b.queue_bytes_photos - a.queue_bytes_photos);

  return (
    <div className="ps-8 pe-8 pbs-8 pbe-8" data-testid="queue-health-page">
      <header className="flex items-center justify-between mbe-6">
        <h1 className="text-2xl font-semibold text-on-surface">
          <FormattedMessage id="LABEL_QUEUE_HEALTH_PAGE" />
        </h1>
      </header>
      {isLoading ? (
        <div data-testid="queue-health-loading">…</div>
      ) : isError ? (
        <div data-testid="queue-health-error">—</div>
      ) : (
        <table
          className="w-full bg-surface-raised border border-divider rounded-md overflow-hidden text-sm"
          data-testid="queue-health-table"
        >
          <thead>
            <tr className="bg-surface border-be border-divider">
              <th className="ps-4 pe-2 pbs-3 pbe-3 text-start font-semibold text-on-surface">
                <FormattedMessage id="LABEL_COL_WORKER" />
              </th>
              <th className="ps-2 pe-2 pbs-3 pbe-3 text-start font-semibold text-on-surface">
                <FormattedMessage id="LABEL_COL_PHOTOS" />
              </th>
              <th className="ps-2 pe-2 pbs-3 pbe-3 text-start font-semibold text-on-surface">
                <FormattedMessage id="LABEL_COL_MB" />
              </th>
              <th className="ps-2 pe-2 pbs-3 pbe-3 text-start font-semibold text-on-surface">
                <FormattedMessage id="LABEL_COL_LAST_SYNC" />
              </th>
              <th className="ps-2 pe-4 pbs-3 pbe-3 text-start font-semibold text-on-surface">
                <FormattedMessage id="LABEL_COL_FREE_DISK" />
              </th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((row) => (
              <tr
                key={row.worker_id}
                className="border-be border-divider"
                data-testid={`queue-health-row-${row.worker_id}`}
              >
                <td className="ps-4 pe-2 pbs-3 pbe-3 font-medium text-on-surface">
                  {row.worker_name}
                </td>
                <td className="ps-2 pe-2 pbs-3 pbe-3 text-text-secondary">
                  {row.queue_depth_photos}
                </td>
                <td className="ps-2 pe-2 pbs-3 pbe-3 text-text-secondary">
                  {formatBytes(row.queue_bytes_photos)}
                </td>
                <td className="ps-2 pe-2 pbs-3 pbe-3 text-text-secondary">
                  {formatDateTime(row.last_sync_success_at)}
                </td>
                <td className="ps-2 pe-4 pbs-3 pbe-3 text-text-secondary">
                  {formatBytes(row.free_disk_bytes)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
